import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronLeft, Sparkles, ShieldCheck, Heart, Award, Tag, Mountain, Trees, Waves, Tent, Flower2, Sunrise, Gem, Frame, Zap } from "lucide-react";
import api from "@/lib/api";
import SearchWidget from "@/components/SearchWidget";
import PropertyCard from "@/components/PropertyCard";
import VideoCarousel from "@/components/VideoCarousel";
import ComingSoonSection from "@/components/ComingSoonSection";
import AdventuresSection from "@/components/AdventuresSection";
import BlogSection from "@/components/BlogSection";
import Membership from "@/components/Membership";
import { inr } from "@/lib/brand";
import HERO_FALLBACK from "@/assets/bannerImg.png";
import HERO_VIDEO from "@/assets/bannerVideo.mp4";

const HEROES = [
  { title: "Sunday Getaway Sale", subtitle: "20% off on some stays" },
  { title: "Crafted Stays, Curated Escapes", subtitle: "Discover extraordinary private retreats across India." },
  { title: "Stay Magnificently", subtitle: "Hand-picked stays. Endless memories." },
];

const COLLECTIONS = [
  { title: "Newly Launched", img: "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=900&q=80", tag: "Top Stays" },
  { title: "Pet Friendly Villas", img: "https://images.unsplash.com/photo-1568084680786-a84f91d1153c?w=900&q=80" },
  { title: "Private Pool Villas", img: "https://images.unsplash.com/photo-1613977257365-aaae5a9817ff?w=900&q=80" },
  { title: "Romantic Getaways", img: "https://images.unsplash.com/photo-1615039666131-964929ad0f1e?w=900&q=80" },
  { title: "Pure Veg Stays", img: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=900&q=80" },
];

export default function HomePage({ onAuth }) {
  const [hero, setHero] = useState(0);
  const [properties, setProperties] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [offers, setOffers] = useState([]);
  const [filterTag, setFilterTag] = useState("All");

  useEffect(() => {
    const t = setInterval(() => setHero((i) => (i + 1) % HEROES.length), 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/properties?featured=true").then((r) => setProperties(r.data)).catch(() => { });
    api.get("/destinations").then((r) => setDestinations(r.data)).catch(() => { });
    api.get("/offers").then((r) => setOffers(r.data)).catch(() => { });
  }, []);

  const filteredProps = filterTag === "All" ? properties : properties.filter(p => p.destination === filterTag);

  const copy = async (code) => {
    try { await navigator.clipboard.writeText(code); alert(`Copied: ${code}`); } catch { }
  };

  return (
    <div data-testid="home-page" className="bg-white">
      {/* HERO BANNER WITH VIDEO BACKGROUND */}
      <section className="relative h-[70vh] md:h-[80vh] min-h-[480px] overflow-hidden">
        <video
          src={HERO_VIDEO} poster={HERO_FALLBACK}
          autoPlay muted loop playsInline preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center text-center">
          <button onClick={() => setHero((hero - 1 + HEROES.length) % HEROES.length)} className="absolute left-6 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-transparent flex items-center justify-center text-white border border-white hover:bg-white hover:text-black" data-testid="hero-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setHero((hero + 1) % HEROES.length)} className="absolute right-6 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-transparent flex items-center justify-center text-white border border-white hover:bg-white hover:text-black" data-testid="hero-next"><ChevronRight className="w-4 h-4" /></button>
          <h1 className="font-display text-4xl md:text-6xl text-white reveal" key={`h-${hero}`}>{HEROES[hero].title}</h1>
          <p className="text-white/90 text-base md:text-lg mt-3 reveal" key={`p-${hero}`}>{HEROES[hero].subtitle}</p>
          <div className="flex gap-1.5 mt-6">
            {HEROES.map((_, i) => (
              <button key={i} onClick={() => setHero(i)} className={`h-1.5 rounded-full transition-all ${i === hero ? "w-8 bg-white" : "w-4 bg-white/50"}`} data-testid={`hero-dot-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* SEARCH PILL */}
      <div className="relative z-10 mt-[-7rem] lg:mt-[-2rem] px-6 lg:px-10">
        <div className="max-w-6xl mx-auto"><SearchWidget /></div>
      </div>

      {/* <div className="h-20" /> */}

      {/* PICK A DESTINATION (dynamic) */}
      {destinations.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 my-16">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-display text-3xl md:text-4xl">Pick a Destination</h2>
            <Link to="/search" className="text-sm font-medium text-stone-600 hover:text-stone-900">Show all destinations →</Link>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-3 md:gap-4" data-testid="destinations-grid">
            {destinations.map((d) => (
              <Link key={d.id} to={`/search?destination=${encodeURIComponent(d.name)}`} className="group flex flex-col items-center text-center p-3 rounded-xl hover:bg-stone-50 transition" data-testid={`destination-${d.slug}`}>
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl overflow-hidden bg-stone-100 mb-2 group-hover:scale-105 transition-transform">
                  <img src={d.image} alt={d.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="text-sm font-semibold">{d.name}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* OFFERS — only render if admin has added any */}
      {offers.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 my-16">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-3xl md:text-4xl">Offers for You</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="offers-grid">
            {offers.map((o) => (
              <div key={o.id} className={`relative bg-gradient-to-br ${o.color || "from-rose-50 to-amber-50"} border border-stone-200 rounded-xl p-5 overflow-hidden`} data-testid={`offer-card-${o.id}`}>
                <div className="badge badge-pink mb-2"><Tag className="w-3 h-3" /> {o.bank}</div>
                <div className="font-display text-xl mt-2">{o.title}</div>
                {o.sub && <div className="text-xs text-stone-600 mt-1">{o.sub}</div>}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-dashed border-stone-300">
                  <div>
                    <div className="text-[0.6rem] text-stone-500 uppercase tracking-wider">Code</div>
                    <div className="font-mono font-semibold text-sm">{o.code}</div>
                  </div>
                  <button onClick={() => copy(o.code)} className="btn-ghost text-sm">Copy</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* BEST RATED VILLAS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 my-16">
        <h2 className="font-display text-3xl md:text-4xl mb-6">Best Rated Villas</h2>
        <div className="flex flex-wrap gap-2 mb-6">
          {["All", ...new Set(properties.map(p => p.destination))].slice(0, 7).map((t) => (
            <button key={t} onClick={() => setFilterTag(t)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${filterTag === t ? "bg-stone-900 text-white" : "bg-white border border-stone-300 hover:bg-stone-50"}`} data-testid={`filter-tag-${t}`}>{t}</button>
          ))}
          <Link to="/search" className="px-4 py-1.5 rounded-full text-sm font-medium bg-white border border-stone-300 hover:bg-stone-50">Explore more →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="featured-grid">
          {filteredProps.slice(0, 8).map((p) => <PropertyCard key={p.id} property={p} onAuth={onAuth} />)}
        </div>
      </section>

      {/* VIDEO CAROUSEL */}
      <VideoCarousel />

      {/* ADVENTURES SECTION */}
      <AdventuresSection />

      {/* COMING SOON DESTINATIONS */}
      <ComingSoonSection />

      {/* COLLECTIONS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 my-16">
        <h2 className="font-display text-3xl md:text-4xl mb-6">Choose a Collection</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {COLLECTIONS.map((c, i) => (
            <Link key={i} to="/search" className="relative aspect-[3/4] rounded-2xl overflow-hidden group">
              <img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              {c.tag && <div className="absolute top-3 left-3 badge badge-pink text-[0.6rem]">{c.tag}</div>}
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white"><div className="font-display text-lg">{c.title}</div></div>
            </Link>
          ))}
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 my-20">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl md:text-4xl">Your Trusted Getaway Partner <Sparkles className="inline w-6 h-6 text-rose-500" /></h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8 text-center">
          {[
            { icon: Award, t: "Curated Stays", d: "Only the best, hand-picked for you." },
            { icon: ShieldCheck, t: "Unmatched Service", d: "Warm hosts, and memorable experiences." },
            { icon: Heart, t: "Impeccable Comfort", d: "Clean, safe and quality-checked stays." },
          ].map((x, i) => (
            <div key={i}>
              <div className="w-14 h-14 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-3"><x.icon className="w-6 h-6 text-stone-700" strokeWidth={1.5} /></div>
              <div className="font-display text-xl">{x.t}</div>
              <div className="text-sm text-stone-600 mt-1">{x.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      {/* <section className="bg-stone-50 py-12 my-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {[{ t: "4.8", s: "Star Ratings" }, { t: "10+", s: "Handpicked Stays" }, { t: "500+", s: "Happy Guests" }].map((x, i) => (
            <div key={i}><div className="font-display text-4xl">{x.t}</div><div className="text-sm text-stone-500 mt-1">{x.s}</div></div>
          ))}
        </div>
      </section> */}

      {/* BLOG SECTION */}
      <BlogSection />

      {/* HOST AN EVENT SECTION */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 my-20">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl md:text-4xl mb-3">Host An Event at One Light Stays</h2>
          <p className="text-stone-500 text-base max-w-xl mx-auto">We provide the Venue & Hospitality — you create unforgettable experiences at our stays across India.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {[
            {
              icon: Flower2,
              title: "Yoga Classes",
              desc: "Serene open-air settings for mindful movement and breathwork.",
              gradient: "from-emerald-50 to-teal-100",
              iconBg: "bg-emerald-100",
              iconColor: "text-emerald-700",
              border: "border-emerald-200",
            },
            {
              icon: Sunrise,
              title: "Wellness Retreats",
              desc: "Rejuvenating getaways in nature for holistic healing.",
              gradient: "from-amber-50 to-orange-100",
              iconBg: "bg-amber-100",
              iconColor: "text-amber-700",
              border: "border-amber-200",
            },
            {
              icon: Gem,
              title: "Destination Weddings",
              desc: "Breathtaking venues for your most cherished celebration.",
              gradient: "from-rose-50 to-pink-100",
              iconBg: "bg-rose-100",
              iconColor: "text-rose-700",
              border: "border-rose-200",
            },
            {
              icon: Frame,
              title: "Art Galleries",
              desc: "Curated spaces to showcase creativity and inspire culture.",
              gradient: "from-violet-50 to-purple-100",
              iconBg: "bg-violet-100",
              iconColor: "text-violet-700",
              border: "border-violet-200",
            },
            {
              icon: Zap,
              title: "Healing Sessions",
              desc: "Tranquil environments for sound baths, therapy & more.",
              gradient: "from-sky-50 to-blue-100",
              iconBg: "bg-sky-100",
              iconColor: "text-sky-700",
              border: "border-sky-200",
            },
          ].map((item, i) => (
            <div
              key={i}
              className={`group relative bg-gradient-to-br ${item.gradient} border ${item.border} rounded-2xl p-6 flex flex-col items-center text-center gap-4 hover:-translate-y-1.5 hover:shadow-lg transition-all duration-300 cursor-pointer`}
            >
              <div className={`w-14 h-14 rounded-2xl ${item.iconBg} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                <item.icon className={`w-7 h-7 ${item.iconColor}`} strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-display text-lg text-stone-900 mb-1">{item.title}</div>
                <div className="text-xs text-stone-500 leading-relaxed">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-stone-900 text-white text-sm font-semibold hover:bg-stone-700 transition-colors duration-200"
          >
            Enquire About Hosting <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* MEMBERSHIP */}
      <Membership onAuth={onAuth} />

      {/* CTA */}
      <section className="my-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="bg-rose-50 border border-rose-100 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <div className="font-display text-2xl">List your property with us</div>
              <div className="text-sm text-stone-600 mt-1">Join our network and turn your property into a high-revenue holiday destination.</div>
            </div>
            <button className="btn-primary" data-testid="list-property-cta"><a href="/become-a-partner" target="_blank" rel="noopener noreferrer">List Now</a></button>
          </div>
        </div>
      </section>
    </div>
  );
}
